import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import TicketList from './components/TicketList';
import ChatInterface from './components/ChatInterface';
import SOSPanel from './components/SOSPanel';
import NewTicketModal from './components/NewTicketModal';

const STATS = [
  { label: 'Open', key: 'open', icon: 'Inbox', color: '#f59e0b', bg: '#fef3c7' },
  { label: 'In Review', key: 'in_review', icon: 'Eye', color: '#3b82f6', bg: '#dbeafe' },
  { label: 'Awaiting', key: 'awaiting_response', icon: 'Clock', color: '#8b5cf6', bg: '#ede9fe' },
  { label: 'Resolved', key: 'resolved', icon: 'CheckCircle', color: '#10b981', bg: '#d1fae5' },
];

const DisputeSupportScreen = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sosAlerts, setSosAlerts] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingSOS, setLoadingSOS] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [activeView, setActiveView] = useState('tickets'); // 'tickets' | 'sos'

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const { data, error } = await supabase?.from('support_tickets')?.select('*')?.order('created_at', { ascending: false });
      if (!error) setTickets(data || []);
    } catch (err) {
      console.error('Fetch tickets error:', err);
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  // Fetch messages for selected ticket
  const fetchMessages = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setLoadingMessages(true);
    try {
      const { data, error } = await supabase?.from('ticket_messages')?.select('*')?.eq('ticket_id', ticketId)?.order('created_at', { ascending: true });
      if (!error) setMessages(data || []);
    } catch (err) {
      console.error('Fetch messages error:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Fetch SOS alerts
  const fetchSOS = useCallback(async () => {
    setLoadingSOS(true);
    try {
      const { data, error } = await supabase?.from('sos_alerts')?.select('*')?.order('created_at', { ascending: false });
      if (!error) setSosAlerts(data || []);
    } catch (err) {
      console.error('Fetch SOS error:', err);
    } finally {
      setLoadingSOS(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchSOS();
  }, [fetchTickets, fetchSOS]);

  // Real-time subscription for messages
  useEffect(() => {
    if (!selectedTicket?.id) return;
    fetchMessages(selectedTicket?.id);

    const channel = supabase?.channel(`ticket-messages-${selectedTicket?.id}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_messages',
        filter: `ticket_id=eq.${selectedTicket?.id}`,
      }, () => {
        fetchMessages(selectedTicket?.id);
      })?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [selectedTicket?.id, fetchMessages]);

  // Real-time subscription for SOS alerts
  useEffect(() => {
    const channel = supabase?.channel('sos-alerts-realtime')?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sos_alerts',
      }, () => {
        fetchSOS();
      })?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [fetchSOS]);

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleStatusChange = (newStatus) => {
    setSelectedTicket(prev => ({ ...prev, ticket_status: newStatus }));
    setTickets(prev => prev?.map(t => t?.id === selectedTicket?.id ? { ...t, ticket_status: newStatus } : t));
  };

  const activeSOSCount = sosAlerts?.filter(a => a?.sos_status !== 'resolved')?.length || 0;

  const statCounts = STATS?.reduce((acc, s) => {
    acc[s.key] = tickets?.filter(t => t?.ticket_status === s?.key)?.length || 0;
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f0f2f8', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Top Header */}
      <div className="sticky top-0 z-30 border-b border-gray-200" style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)' }}>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 hover:opacity-80"
                style={{ background: '#0f2044', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <Icon name="ArrowLeft" size={16} color="#fff" />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.2)' }}>
                <Icon name="Headphones" size={20} color="#D4AF37" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Dispute & Support</h1>
                <p className="text-xs text-blue-200">Customer service management hub</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {activeSOSCount > 0 && (
                <button
                  onClick={() => setActiveView('sos')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg animate-pulse"
                  style={{ background: '#dc2626', color: '#fff' }}
                >
                  <Icon name="AlertTriangle" size={14} />
                  <span className="text-xs font-bold">{activeSOSCount} SOS</span>
                </button>
              )}
              <button
                onClick={() => setShowNewTicket(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:opacity-90"
                style={{ background: '#D4AF37', color: '#0f2044' }}
              >
                <Icon name="Plus" size={16} />
                <span className="hidden sm:inline">New Ticket</span>
              </button>
            </div>
          </div>

          {/* View Tabs */}
          <div className="flex gap-1 pb-0">
            <button
              onClick={() => setActiveView('tickets')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${
                activeView === 'tickets' ? 'border-yellow-400 text-white' : 'border-transparent text-blue-200 hover:text-white'
              }`}
            >
              <Icon name="Ticket" size={15} />
              Tickets
              <span className="px-1.5 py-0.5 rounded-full text-xs" style={{ background: 'rgba(255,255,255,0.15)' }}>
                {tickets?.length}
              </span>
            </button>
            <button
              onClick={() => setActiveView('sos')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${
                activeView === 'sos' ? 'border-red-400 text-white' : 'border-transparent text-blue-200 hover:text-white'
              }`}
            >
              <Icon name="AlertTriangle" size={15} />
              SOS Alerts
              {activeSOSCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs bg-red-500 text-white animate-pulse">
                  {activeSOSCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Stats Bar */}
      <div className="px-4 sm:px-6 lg:px-8 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-4 overflow-x-auto scrollbar-hide">
          {STATS?.map(stat => (
            <div key={stat?.key} className="flex items-center gap-2 flex-shrink-0">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: stat?.bg }}>
                <Icon name={stat?.icon} size={14} style={{ color: stat?.color }} />
              </div>
              <div>
                <p className="text-lg font-bold leading-none" style={{ color: '#0f2044' }}>{statCounts?.[stat?.key] || 0}</p>
                <p className="text-xs text-gray-500">{stat?.label}</p>
              </div>
              <div className="w-px h-8 bg-gray-100 ml-2" />
            </div>
          ))}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-red-50">
              <Icon name="AlertTriangle" size={14} className="text-red-500" />
            </div>
            <div>
              <p className="text-lg font-bold leading-none text-red-600">{activeSOSCount}</p>
              <p className="text-xs text-gray-500">Active SOS</p>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 160px)' }}>
        {activeView === 'tickets' ? (
          <>
            {/* Left: Ticket List */}
            <div className="w-full lg:w-[45%] xl:w-[40%] flex flex-col border-r border-gray-200 bg-white overflow-hidden">
              <TicketList
                tickets={tickets}
                selectedTicket={selectedTicket}
                onSelectTicket={handleSelectTicket}
                loading={loadingTickets}
              />
            </div>

            {/* Right: Chat Interface */}
            <div className="hidden lg:flex flex-1 flex-col bg-white overflow-hidden">
              {loadingMessages && selectedTicket ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
                </div>
              ) : (
                <ChatInterface
                  ticket={selectedTicket}
                  messages={messages}
                  onMessageSent={() => fetchMessages(selectedTicket?.id)}
                  onStatusChange={handleStatusChange}
                />
              )}
            </div>
          </>
        ) : (
          /* SOS View */
          (<div className="flex-1 overflow-hidden">
            <div className="h-full max-w-2xl mx-auto">
              <SOSPanel
                alerts={sosAlerts}
                onAlertUpdate={fetchSOS}
                loading={loadingSOS}
              />
            </div>
          </div>)
        )}
      </div>
      {/* Mobile: Chat overlay when ticket selected */}
      {selectedTicket && activeView === 'tickets' && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white flex flex-col">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100" style={{ background: '#0f2044' }}>
            <button onClick={() => setSelectedTicket(null)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <Icon name="ArrowLeft" size={18} color="#fff" />
            </button>
            <span className="text-sm font-semibold text-white">{selectedTicket?.ticket_number} · {selectedTicket?.subject}</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChatInterface
              ticket={selectedTicket}
              messages={messages}
              onMessageSent={() => fetchMessages(selectedTicket?.id)}
              onStatusChange={handleStatusChange}
            />
          </div>
        </div>
      )}
      {/* New Ticket Modal */}
      <NewTicketModal
        isOpen={showNewTicket}
        onClose={() => setShowNewTicket(false)}
        onTicketCreated={fetchTickets}
      />
    </div>
  );
};

export default DisputeSupportScreen;
